import * as React from 'react';
import type { MessageFormatter, ReceivedChatMessage } from '@livekit/components-react';
import { cn, useChatMessage } from '../../utils/utils';


export interface ChatEntryProps extends React.HTMLAttributes<HTMLLIElement> {
  /** The chat massage object to display. */
  entry: ReceivedChatMessage;
  /** Hide sender name. Useful when displaying multiple consecutive chat messages from the same person. */
  hideName?: boolean;
  /** Hide message timestamp. */
  hideTimestamp?: boolean;
  /** An optional formatter for the message body. */
  messageFormatter?: MessageFormatter;
  /** Explicitly set if the message is from the user. */
  isUser?: boolean;
}

export const ChatEntry = ({
  entry,
  messageFormatter,
  hideName,
  hideTimestamp,
  className,
  isUser: isUserProp,
  ...props
}: ChatEntryProps) => {
  const { message, time, locale } = useChatMessage(entry, messageFormatter);
  const rawSession = localStorage.getItem('chat_session_state');
  let sessionUserName: string | undefined;
  try {
    if (rawSession) {
      const parsed = JSON.parse(rawSession);
      // accept multiple possible key names used across apps
      sessionUserName = parsed?.userName ?? parsed?.username ?? parsed?.name ?? parsed?.user;
    }
  } catch (e) {
    // If parsing fails, the stored value might be a plain string containing the username
    sessionUserName = rawSession || undefined;
  }
  // Strict comparison and guard against undefined
  const isUser = isUserProp ?? Boolean(entry.from?.identity && sessionUserName && entry.from.identity === sessionUserName);

  // Messages coming from the current user should be considered 'local'
  const messageOrigin = isUser ? 'local' : 'remote';
  return (
    <li
      data-lk-message-origin={messageOrigin}
      title={time.toLocaleTimeString(locale, { timeStyle: 'full' })}
      className={cn(
        'group flex flex-col',
        isUser ? 'items-end' : 'items-center',
        className
      )}
      {...props}
    >
      {/* MESSAGE BUBBLE - Modern Clean Style */}
      <div
        className={cn(
          'text-[15px] md:text-base leading-relaxed font-medium',
          isUser ? 'text-primary' : 'text-foreground dark:text-white'
        )}
      >
        <p className="whitespace-pre-wrap break-words text-center">{message}</p>
      </div>

    </li>
  );
};